import { useContext, useMemo } from 'react';
import { PermissionContext } from './PermissionProvider';
import { resolveAccess } from './resolve-access';

/**
 * usePermission — resolve the CRUD-style permission flags for a single menu
 * (SA-1592 Phase A0).
 *
 * Reads the reactive `menus` projection from PermissionContext, resolves the
 * matching menu object via the pure `resolveAccess`, and maps the raw
 * snake_case BE flags (`can_create`, `can_read`, ...) to the camelCase flat
 * shape consumed by `Can` and call sites. Missing menu => all false
 * (fail-closed). Every flag is `Boolean`-coerced so a truthy non-boolean BE
 * value can never leak through.
 *
 * @param {string} keyOrPathname - a MENU key or a raw pathname.
 * @returns {{ canCreate: boolean, canRead: boolean, canUpdate: boolean,
 *   canDelete: boolean, canApprove: boolean, canReadAll: boolean }}
 */
export const usePermission = (keyOrPathname) => {
  const { menus } = useContext(PermissionContext);

  return useMemo(() => {
    const menu = resolveAccess(menus, keyOrPathname);
    return {
      canCreate: Boolean(menu?.can_create),
      canRead: Boolean(menu?.can_read),
      canUpdate: Boolean(menu?.can_update),
      canDelete: Boolean(menu?.can_delete),
      canApprove: Boolean(menu?.can_approve),
      canReadAll: Boolean(menu?.can_read_all),
    };
  }, [menus, keyOrPathname]);
};
